import { useMemo } from 'react';

import { useBreakPoints } from 'core';
import {
  AspectRatio,
  Card,
  Grid,
  Group,
  ImageMantine,
  Skeleton,
  Stack,
  Text,
  centsToDollar,
} from 'ui';

import { useGetHolidayPromotion } from '../api';

const Milestone = ({ min, max, current }: { min: number; max: number; current: number }) => {
  const color = useMemo(() => {
    if (current >= max) {
      // hit
      return 'mint.6';
    } else if (current < min) {
      // locked
      return 'gray.4';
    } else {
      // curent
      return 'cobalt.4';
    }
  }, [current, max, min]);

  const image = useMemo(() => {
    if (current >= max) {
      return 'https://s3.us-east-1.amazonaws.com/com.givebacks.public/images/holidayPromotion/hit-milestone.png';
    } else if (current < min) {
      return 'https://s3.us-east-1.amazonaws.com/com.givebacks.public/images/holidayPromotion/locked-milestone.png';
    } else {
      return 'https://s3.us-east-1.amazonaws.com/com.givebacks.public/images/holidayPromotion/current-milestone.png';
    }
  }, [current, max, min]);

  return (
    <Card withBorder px={'auto'}>
      <Group align="center">
        <AspectRatio ratio={1} w={100} pos="relative" mx={'auto'}>
          <ImageMantine src={image} />
          <Text
            pos="absolute"
            w="100%"
            ta="center"
            c={color}
            fz={16}
            fw={700}
            top={'calc(50% - 10px)'}
          >
            {centsToDollar(max, true)}
          </Text>
        </AspectRatio>
      </Group>
    </Card>
  );
};

const Milestones = () => {
  const { isSmallScreen } = useBreakPoints();
  const { data, isLoading } = useGetHolidayPromotion();
  const current = data?.raised_so_far || 0;
  return (
    <Stack gap={'md'}>
      <Text fw={600} fz={isSmallScreen ? 18 : 24}>
        Donation Milestones
      </Text>

      <Text fw={400} fz={isSmallScreen ? 14 : 16} c={'gray.6'}>
        {"Every milestone unlocked, we'll donate holiday gifts for kids in foster care."}
      </Text>

      <Grid>
        <Grid.Col span={{ base: 6, md: 4 }}>
          <Skeleton visible={isLoading}>
            <Milestone min={0} max={250000} current={current} />
          </Skeleton>
        </Grid.Col>

        <Grid.Col span={{ base: 6, md: 4 }}>
          <Skeleton visible={isLoading}>
            <Milestone min={250000} max={500000} current={current} />
          </Skeleton>
        </Grid.Col>

        <Grid.Col span={{ base: 6, md: 4 }}>
          <Skeleton visible={isLoading}>
            <Milestone min={500000} max={1000000} current={current} />
          </Skeleton>
        </Grid.Col>

        <Grid.Col span={{ base: 6, md: 4 }}>
          <Skeleton visible={isLoading}>
            <Milestone min={1000000} max={1500000} current={current} />
          </Skeleton>
        </Grid.Col>

        <Grid.Col span={{ base: 6, md: 4 }}>
          <Skeleton visible={isLoading}>
            <Milestone min={1500000} max={3000000} current={current} />
          </Skeleton>
        </Grid.Col>

        <Grid.Col span={{ base: 6, md: 4 }}>
          <Skeleton visible={isLoading}>
            <Milestone min={3000000} max={5000000} current={current} />
          </Skeleton>
        </Grid.Col>
      </Grid>
    </Stack>
  );
};
export { Milestones };
